import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppLogo } from "@/components/AppLogo";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { ROLE_HOME, useAuth, type AppRole } from "@/lib/auth";
import { useState } from "react";
import { toast } from "sonner";

import { authErrorMessage, safeAuth } from "@/lib/auth-errors";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in · Landoo" },
      {
        name: "description",
        content: "Sign in to Landoo to save favourites, unlock contacts and manage listings.",
      },
      { property: "og:title", content: "Sign in · Landoo" },
      { property: "og:description", content: "Sign in to Landoo." },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const nav = useNavigate();
  const { refreshProfile } = useAuth();
  const [mode, setMode] = useState<"email" | "mobile">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const routeAfterLogin = async (uid: string) => {
    const { data } = await supabase.from("user_roles").select("role").eq("user_id", uid);
    const roles = (data ?? []).map((r) => r.role as AppRole);
    const primary: AppRole =
      roles.find((r) => r === "super_admin") ??
      roles.find((r) => r === "admin") ??
      roles.find((r) => r === "partner") ??
      "customer";
    await refreshProfile();
    nav({ to: ROLE_HOME[primary], replace: true });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    let data, error;
    try {
      ({ data, error } = await supabase.auth.signInWithPassword({ email, password }));
    } catch (err) {
      setSubmitting(false);
      return toast.error(authErrorMessage(err));
    }
    setSubmitting(false);
    if (error) return toast.error(authErrorMessage(error));
    if (!data) return;
    if (data.user) await routeAfterLogin(data.user.id);
  };

  const sendOtp = async () => {
    if (!/^\+\d{8,15}$/.test(mobile))
      return toast.error("Enter mobile in international format, e.g. +919876543210");
    setSubmitting(true);
    const { error } = await safeAuth(() => supabase.auth.signInWithOtp({ phone: mobile }));
    setSubmitting(false);
    if (error) return toast.error(error);
    setOtpSent(true);
    toast.success("OTP sent to your mobile.");
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { data, error } = await safeAuth(() =>
      supabase.auth.verifyOtp({ phone: mobile, token: otp, type: "sms" }),
    );
    setSubmitting(false);
    if (error) return toast.error(error);
    if (data?.user) await routeAfterLogin(data.user.id);
  };

  const onGoogle = async () => {
    // OAuth can reject (popup blocked, network) as well as resolve with an error.
    const { data: result, error } = await safeAuth(
      () =>
        lovable.auth.signInWithOAuth("google", {
          redirect_uri: window.location.origin,
        }) as Promise<{ error?: unknown; redirected?: boolean }>,
      "Google sign-in failed",
    );
    if (error) return toast.error(error);
    if (!result || result.redirected) return;
    const { data: userData, error: userError } = await safeAuth(
      () => supabase.auth.getUser(),
      "Google sign-in failed",
    );
    if (userError) return toast.error(userError);
    if (userData?.user) await routeAfterLogin(userData.user.id);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream/40 p-4">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-6 shadow-float">
        <div className="mb-4">
          <AppLogo />
        </div>
        <h1 className="text-xl font-bold text-brown-dark">Welcome back</h1>
        <p className="mt-1 text-sm text-muted-foreground">Sign in to continue.</p>

        <button
          type="button"
          onClick={onGoogle}
          className="mt-5 w-full rounded-xl border border-border bg-background py-2.5 text-sm font-semibold text-brown-dark shadow-soft transition hover:bg-brand-soft"
        >
          Continue with Google
        </button>

        <button
          type="button"
          disabled
          title="Apple sign-in is coming soon"
          className="mt-2 w-full rounded-xl border border-dashed border-border bg-background py-2.5 text-sm font-semibold text-muted-foreground disabled:cursor-not-allowed"
        >
          Continue with Apple · coming soon
        </button>

        <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground">
          <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
        </div>

        <div className="mb-3 grid grid-cols-2 gap-1 rounded-xl bg-secondary p-1 text-xs font-semibold">
          {(["email", "mobile"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-lg py-1.5 transition ${mode === m ? "bg-card text-brown-dark shadow-soft" : "text-muted-foreground"}`}
            >
              {m === "email" ? "Email & password" : "Mobile OTP"}
            </button>
          ))}
        </div>

        {mode === "mobile" ? (
          <form className="space-y-3" onSubmit={verifyOtp}>
            <input
              type="tel"
              required
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Mobile number (+91…)"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
            />
            {otpSent && (
              <input
                inputMode="numeric"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="6-digit OTP"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
              />
            )}
            {otpSent ? (
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-coral py-2.5 text-sm font-semibold text-white shadow-soft transition hover:brightness-105 disabled:opacity-50"
              >
                {submitting ? "Verifying…" : "Verify & sign in"}
              </button>
            ) : (
              <button
                type="button"
                onClick={sendOtp}
                disabled={submitting}
                className="w-full rounded-xl bg-coral py-2.5 text-sm font-semibold text-white shadow-soft transition hover:brightness-105 disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Send OTP"}
              </button>
            )}
            <p className="text-[11px] text-muted-foreground">
              SMS delivery requires an SMS provider to be configured for the project.
            </p>
          </form>
        ) : (
          <form className="space-y-3" onSubmit={onSubmit}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-coral py-2.5 text-sm font-semibold text-white shadow-soft transition hover:brightness-105 disabled:opacity-50"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}

        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <Link to="/register" className="font-semibold text-brown-dark hover:underline">
            Create account
          </Link>
          <Link to="/" className="hover:underline">
            Back to map
          </Link>
        </div>
      </div>
    </div>
  );
}
